"use client";

import { c as _c } from "react/compiler-runtime";
import { useParams } from "next/navigation";
import { useRouter } from "nextjs-toploader/app";
import { fallbackLng, fallbackLocale } from "./i18n/settings";
import { mapWebLangToServerLang } from "./i18n/utils";
export function getCurrentLocaleInClient() {
  const pathParts = window.location.pathname.split("/");
  if (pathParts.length > 1 && pathParts[1]) {
    return pathParts[1];
  }
  return "";
}
export function getValueFromTranslations<T = string>(lang: string, translations?: Record<string, T> | null) {
  if (typeof translations === "string") {
    return translations;
  }
  if (!translations) {
    return "";
  }
  const _lang = mapWebLangToServerLang(lang);

  // 直接匹配当前语言，如果有值就直接返回
  if (translations[_lang]) {
    return translations[_lang];
  }

  // 如果当前语言没有值，才进行兜底逻辑
  const localeKeys = Object.keys(translations);

  // 优先级匹配：英语 -> 繁体中文 -> 简体中文 -> 第一个可用值
  const priorityOrder = ["en-us", "zh-hant", "zh-hans"];
  for (const priority of priorityOrder) {
    if (translations[priority]) {
      return translations[priority];
    }
  }

  // 如果所有优先级都没有匹配，尝试语言前缀匹配
  const languagePrefix = _lang.split("-")[0];
  for (const key of localeKeys) {
    if (key.startsWith(languagePrefix)) {
      return translations[key];
    }
  }

  // 最后返回第一个可用的值
  const firstAvailableKey = localeKeys[0];
  return firstAvailableKey ? translations[firstAvailableKey] : "";
}

/**
 * 获取当前区域代码
 * @returns 当前区域代码，默认为配置中的 fallbackLocale
 */
export function getCurrentAreaInClient(): string {
  // 仅在客户端环境执行
  if (typeof window === "undefined") {
    return fallbackLocale.toUpperCase(); // 使用配置的默认值
  }
  try {
    // 从 cookie 获取区域信息
    const match = document.cookie.match(/(^| )EA_REGION=([^;]+)/);
    if (match?.[2]) {
      return match[2].toUpperCase();
    }

    // 从 URL 路径获取区域信息
    const pathname = window.location.pathname;
    // 清理路径中的多余斜杠
    const cleanPathname = pathname.replace(/\/+/g, "/");
    const segments = cleanPathname.split("/");
    // 过滤掉空字符串段
    const validSegments = segments.filter(segment => segment.trim() !== "");
    if (validSegments.length > 0 && validSegments[0]) {
      const localeSegment = validSegments[0];
      // 处理形如 "en-HK" 的情况，提取 HK 部分
      if (localeSegment.includes("-")) {
        const parts = localeSegment.split("-");
        return parts[parts.length - 1].toUpperCase();
      }
      // 处理形如 "hk" 的情况，直接转大写
      else {
        return localeSegment.toUpperCase();
      }
    }
  } catch (error) {
    console.error("获取区域信息失败:", error);
  }
  return fallbackLocale.toUpperCase(); // 使用配置的默认值
}

/**
 * 获取当前客户端的语言设置 - 基于 atom 状态
 * @returns 当前语言代码，优先使用 atom 中的状态
 */
export function getCurrentLanguageInClient(): string {
  // 仅在客户端环境执行
  if (typeof window === "undefined") {
    return fallbackLng;
  }
  try {
    // 1. 优先从 atom 获取语言设置（从 localStorage）
    const storedLanguage = localStorage.getItem("current_language");
    if (storedLanguage) {
      // 清理可能的引号，确保返回干净的字符串
      return storedLanguage.replace(/['"]/g, "").toLowerCase();
    }

    // 2. 从 URL 获取语言
    const pathParts = window.location.pathname.split("/");
    if (pathParts.length > 1 && pathParts[1]) {
      const localeFromUrl = pathParts[1];
      if (localeFromUrl && localeFromUrl !== "api") {
        if (localeFromUrl.includes("-")) {
          // 这是语言代码，清理可能的引号后返回
          return localeFromUrl.replace(/['"]/g, "");
        } else {
          // 这是地区代码，使用默认语言
          return fallbackLng;
        }
      }
    }
  } catch (error) {
    console.error("获取语言信息失败:", error);
  }

  // 3. 使用配置中的默认语言
  return fallbackLng;
}
export function getCurrentLangInClient() {
  const lang = getCurrentLanguageInClient();
  return mapWebLangToServerLang(lang);
}
export function getCurrentDevice() {
  const userAgent = window.navigator.userAgent;
  return userAgent.includes("Mobile") ? "mob" : "pc";
}
export function useRouterPush() {
  const $ = _c(3);
  const router = useRouter();
  const {
    locale
  } = useParams();
  let t0;
  if ($[0] !== locale || $[1] !== router) {
    t0 = href => {
      const safeHref = typeof href === "string" ? href : "";
      const safeLocale = typeof locale === "string" && locale.trim() !== "" ? locale : fallbackLocale;
      const cleanHref = safeHref.replace(/\/+/g, "/");
      if (cleanHref.startsWith(`/${safeLocale}/`) || cleanHref === `/${safeLocale}`) {
        router.push(cleanHref);
      } else {
        if (cleanHref.startsWith("/")) {
          router.push(`/${safeLocale}${cleanHref}`);
        } else {
          router.push(cleanHref);
        }
      }
    };
    $[0] = locale;
    $[1] = router;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}

/**
 * 带地区代码的 Router Hook
 * 自动在 push、replace 方法中拼接地区代码
 */
export function useLocaleRouter() {
  const $ = _c(14);
  const router = useRouter();
  const {
    locale
  } = useParams();
  let t0;
  if ($[0] !== locale) {
    t0 = typeof locale === "string" && locale.trim() !== "" ? locale : fallbackLocale;
    $[0] = locale;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const safeLocale = t0;
  let t1;
  if ($[2] !== safeLocale) {
    t1 = href => {
      const safeHref = typeof href === "string" ? href : "";
      const cleanHref = safeHref.replace(/\/+/g, "/");
      if (cleanHref.startsWith(`/${safeLocale}/`) || cleanHref === `/${safeLocale}`) {
        return cleanHref;
      } else {
        if (cleanHref.startsWith("/")) {
          return `/${safeLocale}${cleanHref}`;
        } else {
          return cleanHref;
        }
      }
    };
    $[2] = safeLocale;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const processHref = t1;
  let t2;
  let t3;
  let t4;
  if ($[4] !== processHref || $[5] !== router) {
    t2 = (href_0, options) => router.push(processHref(href_0), options);
    t3 = (href_1, options_0) => router.replace(processHref(href_1), options_0);
    t4 = href_2 => router.prefetch(processHref(href_2));
    $[4] = processHref;
    $[5] = router;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
  }
  let t5;
  if ($[9] !== router || $[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
    t5 = {
      ...router,
      push: t2,
      replace: t3,
      prefetch: t4
    };
    $[9] = router;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}

/**
 * 根据当前地区获取默认货币
 * @returns 当前地区对应的货币代码
 */
export function getDefaultCurrencyByRegion(): string {
  const region = getCurrentAreaInClient();
  switch (region) {
    case "CN":
      return "CNY";
    case "GB":
      return "GBP";
    case "TW":
      return "TWD";
    case "MO":
      return "MOP";
    case "HK":
    default:
      return "GBP";
  }
}

/**
 * Fix mobile viewport issues after keyboard dismissal
 * Specifically addresses Android keyboard viewport problems
 */
export function fixMobileViewport() {
  if (typeof window === "undefined") return;

  // Only apply on mobile devices
  if (!/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
    return;
  }

  // Force layout recalculation
  const originalHeight = document.documentElement.style.height;
  document.documentElement.style.height = "auto";
  void document.documentElement.offsetHeight;
  document.documentElement.style.height = originalHeight;

  // Reset body height
  const originalBodyHeight = document.body.style.height;
  document.body.style.height = "auto";
  void document.body.offsetHeight;
  document.body.style.height = originalBodyHeight;

  // Scroll to top
  window.scrollTo(0, 0);

  // Dispatch resize event
  window.dispatchEvent(new Event("resize"));
}

/**
 * 基于area的localStorage工具函数
 */

/**
 * 获取基于area的localStorage key
 * @param baseKey 基础key
 * @param area 区域代码，如果不提供则使用当前area
 * @returns 带area前缀的key
 */
function getAreaBasedKey(baseKey: string, area?: string): string {
  const currentArea = area || getCurrentAreaInClient();
  return `${currentArea}_${baseKey}`;
}

/**
 * 获取基于area的localStorage数据
 * @param baseKey 基础key
 * @param area 区域代码，如果不提供则使用当前area
 * @returns 存储的值，如果不存在则返回null
 */
export function getAreaBasedStorage(baseKey: string, area?: string): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const key = getAreaBasedKey(baseKey, area);
    return localStorage.getItem(key);
  } catch (error) {
    console.error("Error getting area-based storage:", error);
    return null;
  }
}

/**
 * 设置基于area的localStorage数据
 * @param baseKey 基础key
 * @param value 要存储的值
 * @param area 区域代码，如果不提供则使用当前area
 */
export function setAreaBasedStorage(baseKey: string, value: string, area?: string): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    const key = getAreaBasedKey(baseKey, area);
    localStorage.setItem(key, value);
  } catch (error) {
    console.error("Error setting area-based storage:", error);
  }
}

/**
 * 删除基于area的localStorage数据
 * @param baseKey 基础key
 * @param area 区域代码，如果不提供则使用当前area
 */
export function removeAreaBasedStorage(baseKey: string, area?: string): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    const key = getAreaBasedKey(baseKey, area);
    localStorage.removeItem(key);
  } catch (error) {
    console.error("Error removing area-based storage:", error);
  }
}

/**
 * 获取基于area的JSON数据
 * @param baseKey 基础key
 * @param area 区域代码，如果不提供则使用当前area
 * @returns 解析后的JSON数据，如果不存在或解析失败则返回null
 */
export function getAreaBasedJsonStorage<T>(baseKey: string, area?: string): T | null {
  const rawValue = getAreaBasedStorage(baseKey, area);
  if (!rawValue) {
    return null;
  }
  try {
    return JSON.parse(rawValue);
  } catch (error) {
    console.error("Error parsing area-based JSON storage:", error);
    return null;
  }
}

/**
 * 设置基于area的JSON数据
 * @param baseKey 基础key
 * @param value 要存储的数据
 * @param area 区域代码，如果不提供则使用当前area
 */
export function setAreaBasedJsonStorage<T>(baseKey: string, value: T, area?: string): void {
  try {
    const jsonValue = JSON.stringify(value);
    setAreaBasedStorage(baseKey, jsonValue, area);
  } catch (error) {
    console.error("Error stringifying area-based JSON storage:", error);
  }
}

// 自定义storage对象，实现基于area的存储
export const areaBasedStorage = {
  getItem: (key: string, initialValue: boolean): boolean => {
    if (typeof window === "undefined") {
      return initialValue;
    }
    const result = getAreaBasedJsonStorage<boolean>(key);
    return result ?? initialValue;
  },
  setItem: (key: string, value: boolean): void => {
    if (typeof window === "undefined") {
      return;
    }
    setAreaBasedJsonStorage(key, value);
  },
  removeItem: (key: string): void => {
    if (typeof window === "undefined") {
      return;
    }
    setAreaBasedJsonStorage(key, null);
  }
};
export function fixNodeOperationErrors() {
  if (typeof Node === "function" && Node.prototype) {
    const originalRemoveChild = Node.prototype.removeChild;
    Node.prototype.removeChild = function <T extends Node>(child: T): T {
      if (child.parentNode !== this) {
        console.warn("Attempted to remove a child node from the wrong parent. Skipping.", {
          childNode: child?.nodeName,
          expectedParent: this?.nodeName,
          actualParent: child?.parentNode?.nodeName
        });
        return child;
      }
      return originalRemoveChild.call(this, child) as T;
    };
    const originalInsertBefore = Node.prototype.insertBefore;
    Node.prototype.insertBefore = function <T extends Node>(newNode: T, referenceNode: Node | null): T {
      if (referenceNode && referenceNode.parentNode !== this) {
        console.warn("Attempted to insert before a reference node from the wrong parent. Skipping.", {
          newNode: newNode?.nodeName,
          expectedParent: this?.nodeName,
          actualParent: referenceNode?.parentNode?.nodeName
        });
        return newNode;
      }
      return originalInsertBefore.call(this, newNode, referenceNode) as T;
    };
  }
}